import { createReducer, on, createFeature, provideState } from '@ngrx/store';
import { makeEnvironmentProviders } from '@angular/core';
import { provideEffects } from '@ngrx/effects';
import { VaButtonActions } from './va-button.actions';
import { VaButtonEffects } from './va-button.effects';
import { WindowStateActions } from '../window/window.actions';
import { SettingsActions } from '../settings/settings.action';
import { MainComponentActions } from '../main.actions';
import { ChatHeaderActions } from '../chat-header/chat-header.actions';
import { FloatingButtonStates, Language } from '../../types/enum';
import { AuthTokenActions } from '../auth-token/auth-token.actions';

export const initialState: Readonly<VaButtonState> = {
  chatAppBrand: '',
  minimizedHeaderTitle: '',
  language: Language.en,
  isFloatingButtonEnabled: false,
  floatingButtonState: FloatingButtonStates.FLOATING,
  isScrolled: false,
  isLiveChat: false,
  popOverText: '',
  agentName: '',
  buttonText: '',
  unreadMessageCount: 0,
  logoImageUrl: '',
};

export const vaButtonFeature = createFeature({
  name: 'vaButton',
  reducer: createReducer(
    initialState,
    on(WindowStateActions.openWindow, (state) => {
      return {
        ...state,
        floatingButtonState: FloatingButtonStates.OPEN,
        unreadMessageCount: 0,
        popOverText: '',
      };
    }),
    on(AuthTokenActions.startSession, (state) => {
      return {
        ...state,
        floatingButtonState: FloatingButtonStates.OPEN,
        unreadMessageCount: 0,
        popOverText: '',
        isSessionExpired: false,
      };
    }),
    on(VaButtonActions.minimize, (state, { isLiveChat, agentName }) => {
      return {
        ...state,
        floatingButtonState: FloatingButtonStates.MINIMIZED,
        isLiveChat,
        agentName,
      };
    }),
    on(WindowStateActions.closeWindow, (state) => {
      return {
        ...state,
        floatingButtonState: FloatingButtonStates.FLOATING,
      };
    }),
    on(VaButtonActions.onSessionExpired, (state, { popOverText }) => {
      return {
        ...state,
        popOverText,
        floatingButtonState: FloatingButtonStates.FLOATING,
      };
    }),
    on(SettingsActions.setLanguageSuccess, (state, { language }) => {
      return {
        ...state,
        language,
      };
    }),
    on(
      VaButtonActions.updateUnreadMessageCount,
      (state, { agentName, unreadMessageCount }) => {
        return {
          ...state,
          agentName,
          unreadMessageCount,
        };
      }
    ),
    on(
      VaButtonActions.enableOrDisableFloatingButton,
      (state, { isFloatingButtonEnabled }) => {
        return {
          ...state,
          isFloatingButtonEnabled:
            isFloatingButtonEnabled && !isOldVaOpenOrMinimized(),
        };
      }
    ),

    on(VaButtonActions.setButtonText, (state, { buttonText }) => {
      return {
        ...state,
        buttonText,
      };
    }),
    on(
      VaButtonActions.setMinimizedHeaderTitle,
      (state, { minimizedHeaderTitle }) => {
        return {
          ...state,
          minimizedHeaderTitle,
        };
      }
    ),
    on(MainComponentActions.mainComponentLoaded, (state) => {
      return {
        ...state,
        popOverText: '',
      };
    }),
    on(ChatHeaderActions.setHeader, (state, { value }) => {
      return { ...state, logoImageUrl: document.baseURI + value.logo };
    }),
    on(ChatHeaderActions.setLogo, (state, { value }) => {
      return { ...state, logoImageUrl: document.baseURI + value };
    }),
    on(VaButtonActions.setLogoUrl, (state, { logoImageUrl }) => {
      return { ...state, logoImageUrl: document.baseURI + logoImageUrl };
    }),
    on(VaButtonActions.setChatAppBrand, (state, { chatAppBrand }) => {
      return { ...state, chatAppBrand };
    })
  ),
});

function isOldVaOpenOrMinimized(): boolean {
  const oldVaState = localStorage.getItem('va-chat-state');
  return (
    oldVaState == FloatingButtonStates.OPEN ||
    oldVaState == FloatingButtonStates.MINIMIZED
  );
}

export const { selectVaButtonState } = vaButtonFeature;

export function provideVaButtonFeature() {
  return makeEnvironmentProviders([
    provideState(vaButtonFeature),
    provideEffects(VaButtonEffects),
  ]);
}
